import React, { useEffect, useState } from 'react';
import { database } from '../firebaseConfig';
import { ref, get, set } from 'firebase/database';

const GOALS = [
  { key: 'homesToSell', label: 'How many homes do you want to sell this year?', type: 'number', placeholder: 'e.g., 12' },
  { key: 'incomeGoal', label: 'What is your gross commissionable income goal for the year?', type: 'money', placeholder: 'e.g., 150000' },
  { key: 'listingAppointmentsPerMonth', label: 'Listing appointments per month', type: 'number', placeholder: 'e.g., 4' },
  { key: 'buyerConsultationsPerMonth', label: 'Buyer consultations per month', type: 'number', placeholder: 'e.g., 6' },
  { key: 'reachOutsPerWeek', label: 'Reach outs per week', type: 'number', placeholder: '50' },
  { key: 'hotLeadsGoal', label: 'Hot leads to keep active at one time', type: 'number', placeholder: 'e.g., 10' },
];

const card = { background: '#ffffff', border: '1px solid #d8c9a8', borderRadius: 10, padding: 20, marginBottom: 16 };
const h2 = { fontSize: 18, fontWeight: 700, color: '#1a1a1a', margin: '0 0 12px' };
const muted = { color: '#7f7f7f', fontSize: 14, margin: '0 0 16px', lineHeight: 1.5 };
const label = { display: 'block', fontSize: 14, fontWeight: 600, color: '#1a1a1a', marginBottom: 6 };
const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d8c9a8', borderRadius: 6, fontSize: 15, color: '#1a1a1a' };
const hint = { fontSize: 12, color: '#7f7f7f', marginTop: 4 };
const btn = { backgroundColor: '#1a1a1a', color: '#D4AF37', border: 'none', padding: '10px 18px', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 15 };

// Goals for one user, shown inside My Profile. uid is the account whose goals are shown.
export default function GoalsSection({ uid }) {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!uid) return;
    setLoading(true);
    (async () => {
      try {
        const snap = await get(ref(database, `users/${uid}/goals`));
        setValues(snap.exists() ? snap.val() : {});
      } catch (err) {
        console.error('Could not load goals:', err);
        setError('Could not load goals.');
      } finally {
        setLoading(false);
      }
    })();
  }, [uid]);

  const change = (key, value) => setValues((v) => ({ ...v, [key]: value }));

  const save = async () => {
    setError('');
    setMsg('');
    setSaving(true);
    try {
      const payload = { updatedAt: new Date().toISOString() };
      GOALS.forEach((g) => {
        const raw = String(values[g.key] ?? '').replace(/[^0-9.]/g, '');
        payload[g.key] = raw === '' ? null : Number(raw);
      });
      await set(ref(database, `users/${uid}/goals`), payload);
      setMsg('Goals saved.');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      console.error('Could not save goals:', err);
      setError('Could not save goals. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={card}>
      <h2 style={h2}>My Goals</h2>
      <p style={muted}>Set your targets for the year. These show up on your dashboard as you start tracking results.</p>
      {msg && <p style={{ color: '#2e7d32', fontSize: 14 }}>{msg}</p>}
      {error && <p style={{ color: '#b00020', fontSize: 14 }}>{error}</p>}
      {loading ? (
        <p style={muted}>Loading goals...</p>
      ) : (
        <>
          {GOALS.map((g) => (
            <div key={g.key} style={{ marginBottom: 18 }}>
              <label style={label} htmlFor={`goal-${g.key}`}>{g.label}</label>
              <input
                id={`goal-${g.key}`}
                inputMode="numeric"
                placeholder={g.placeholder}
                value={values[g.key] ?? ''}
                onChange={(e) => change(g.key, g.type === 'money' ? e.target.value.replace(/[^0-9.]/g, '') : e.target.value)}
                style={input}
              />
              {g.type === 'money' && <div style={hint}>Enter the dollar amount, numbers only.</div>}
            </div>
          ))}
          <button type="button" onClick={save} disabled={saving} style={{ ...btn, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving...' : 'Save goals'}
          </button>
        </>
      )}
    </div>
  );
}
